import * as asyncStorage from '#platform/server/asyncStorage';
import { logger } from '#platform/server/log';
import { createApp } from '#server/app';
import * as db from '#server/db';
import { PostError } from '#server/errors';
import { fromDateRepr, toDateRepr } from '#server/models';
import { mutator, runMutator } from '#server/mutators';
import { post } from '#server/post';
import * as prefs from '#server/prefs';
import { getServer } from '#server/server-config';
import * as sheet from '#server/sheet';
import { batchMessages } from '#server/sync';
import { undoable } from '#server/undo';
import { currentDay } from '#shared/months';
import type { ExchangeRateEntity } from '#types/models';

import { julianDayOfInt } from './sql';

export type ExchangeRatesHandlers = {
  'exchange-rates-get': typeof getExchangeRates;
  'exchange-rate-create': typeof createExchangeRate;
  'exchange-rate-update': typeof updateExchangeRate;
  'exchange-rate-delete': typeof deleteExchangeRate;
  'exchange-rates-provider-status': typeof providerStatus;
  'exchange-rates-fetch': typeof fetchRates;
};

export const app = createApp<ExchangeRatesHandlers>();
app.method('exchange-rates-get', getExchangeRates);
app.method('exchange-rate-create', mutator(undoable(createExchangeRate)));
app.method('exchange-rate-update', mutator(undoable(updateExchangeRate)));
app.method('exchange-rate-delete', mutator(undoable(deleteExchangeRate)));
app.method('exchange-rates-provider-status', providerStatus);
app.method('exchange-rates-fetch', fetchRates);

function toEntity(row: db.DbExchangeRate): ExchangeRateEntity {
  return {
    id: row.id,
    source_currency: row.source_currency,
    target_currency: row.target_currency,
    rate: row.rate,
    date: fromDateRepr(row.date),
    source: row.source ?? null,
  };
}

async function getExchangeRates(): Promise<ExchangeRateEntity[]> {
  const rows = await db.getExchangeRates();
  return rows.map(toEntity);
}

function validate(rate: Partial<ExchangeRateEntity>) {
  if (
    rate.source_currency !== undefined ||
    rate.target_currency !== undefined
  ) {
    if (!rate.source_currency || !rate.target_currency) {
      throw new Error('Both currencies are required');
    }
    if (rate.source_currency === rate.target_currency) {
      throw new Error('Source and target currency must differ');
    }
  }
  if (rate.rate !== undefined) {
    if (!Number.isFinite(rate.rate) || rate.rate <= 0) {
      throw new Error('Rate must be a positive number');
    }
  }
}

/**
 * Converted amounts are computed on the fly from the rates table, so the
 * budget sheet has to be recomputed whenever rates change.
 */
function recomputeBudget() {
  try {
    sheet.get().recomputeAll();
  } catch {
    // No spreadsheet loaded (e.g. in tests); nothing to recompute.
  }
}

async function createExchangeRate(
  rate: Omit<ExchangeRateEntity, 'id' | 'tombstone'>,
): Promise<ExchangeRateEntity['id']> {
  validate(rate);
  const id = await db.insertExchangeRate({
    source_currency: rate.source_currency,
    target_currency: rate.target_currency,
    rate: rate.rate,
    date: toDateRepr(rate.date),
    source: rate.source ?? 'manual',
  });
  recomputeBudget();
  return id;
}

async function updateExchangeRate(
  rate: Pick<ExchangeRateEntity, 'id'> & Partial<ExchangeRateEntity>,
): Promise<void> {
  validate(rate);
  const { id, date, tombstone: _tombstone, ...rest } = rate;
  await db.updateExchangeRate({
    id,
    ...rest,
    ...(date !== undefined && { date: toDateRepr(date) }),
  });
  recomputeBudget();
}

async function deleteExchangeRate({
  id,
}: Pick<ExchangeRateEntity, 'id'>): Promise<ExchangeRateEntity['id']> {
  await db.deleteExchangeRate(id);
  recomputeBudget();
  return id;
}

/**
 * Rate to multiply an amount in `from` by to express it in `to`, using the
 * stored rate closest in time to `date` (yyyymmdd integer or 'YYYY-MM-DD').
 * Returns null when no rate exists for the pair in either direction.
 */
export async function getRateForDate(
  from: string,
  to: string,
  date: number | string,
): Promise<number | null> {
  if (from === to) {
    return 1;
  }
  const dateStr = typeof date === 'number' ? fromDateRepr(date) : date;
  const row = await db.first<{ rate: number }>(
    `SELECT CASE WHEN r.source_currency = ? THEN r.rate ELSE 1.0 / r.rate END AS rate
       FROM exchange_rates r
      WHERE r.tombstone = 0
        AND ((r.source_currency = ? AND r.target_currency = ?)
          OR (r.source_currency = ? AND r.target_currency = ?))
      ORDER BY ABS(${julianDayOfInt('r.date')} - julianday(?)) ASC,
               CASE WHEN r.source_currency = ? THEN 0 ELSE 1 END ASC,
               r.date DESC
      LIMIT 1`,
    [from, from, to, to, from, dateStr, from],
  );
  return row?.rate ?? null;
}

/** Most recently dated rate for the pair, or null when none is stored. */
export async function getLatestRate(
  from: string,
  to: string,
): Promise<number | null> {
  if (from === to) {
    return 1;
  }
  const row = await db.first<{ rate: number }>(
    `SELECT CASE WHEN r.source_currency = ? THEN r.rate ELSE 1.0 / r.rate END AS rate
       FROM exchange_rates r
      WHERE r.tombstone = 0
        AND ((r.source_currency = ? AND r.target_currency = ?)
          OR (r.source_currency = ? AND r.target_currency = ?))
      ORDER BY r.date DESC,
               CASE WHEN r.source_currency = ? THEN 0 ELSE 1 END ASC
      LIMIT 1`,
    [from, from, to, to, from, from],
  );
  return row?.rate ?? null;
}

/** The budget's default currency code ('' when none is configured). */
export async function getBudgetCurrency(): Promise<string> {
  const row = await db.first<{ value: string | null }>(
    `SELECT value FROM preferences WHERE id = 'defaultCurrencyCode'`,
  );
  return row?.value ?? '';
}

/** An account's effective currency: its own, or the budget default. */
export async function getAccountCurrency(
  accountId: string,
  budgetCurrency?: string,
): Promise<string> {
  const row = await db.first<{ currency: string | null }>(
    `SELECT currency FROM accounts WHERE id = ?`,
    [accountId],
  );
  return row?.currency || budgetCurrency || (await getBudgetCurrency());
}

// ---------------------------------------------------------------------------
// Automatic rates from Open Exchange Rates (via the sync server)
// ---------------------------------------------------------------------------

/** `source` stored on rows fetched from the provider. */
export const OPENEXCHANGERATES_SOURCE = 'openexchangerates';

/** Don't re-check whether the provider is configured more than hourly. */
const AUTO_FETCH_RECHECK_MS = 60 * 60 * 1000;

export type ExchangeRateProviderStatus = {
  configured: boolean;
  error?: string;
};

type FetchRatesArgs = {
  /** yyyy-MM-dd to fetch historical rates for; omit for the latest rates */
  date?: string;
};

export type FetchRatesResult =
  | {
      ok: true;
      /** day the rates were stored under */
      date: string;
      base: string;
      /** currencies a rate was stored (or refreshed) for */
      currencies: string[];
      /** currencies left alone because a manual rate exists for that day */
      skipped: string[];
    }
  | {
      ok: false;
      /**
       * 'unauthorized' | 'no-server' | 'no-budget-currency' |
       * 'no-foreign-accounts' | 'request-failed', or an error code from the
       * sync server (NOT_CONFIGURED, INVALID_APP_ID, RATE_LIMITED, ...)
       */
      error: string;
      reason?: string;
    };

async function serverHeaders(): Promise<
  { headers: Record<string, string>; url: string } | { error: string }
> {
  const userToken = await asyncStorage.getItem('user-token');
  if (!userToken) {
    return { error: 'unauthorized' };
  }
  const serverConfig = getServer();
  if (!serverConfig) {
    return { error: 'no-server' };
  }
  return {
    url: serverConfig.OPENEXCHANGERATES_SERVER,
    headers: { 'X-ACTUAL-TOKEN': userToken },
  };
}

/** Whether the sync server has an Open Exchange Rates App ID stored. */
async function providerStatus(): Promise<ExchangeRateProviderStatus> {
  const server = await serverHeaders();
  if ('error' in server) {
    return { configured: false, error: server.error };
  }
  try {
    const data = await post(server.url + '/status', {}, server.headers);
    return { configured: Boolean(data?.configured) };
  } catch (e) {
    return {
      configured: false,
      error: e instanceof PostError ? e.reason : 'request-failed',
    };
  }
}

/** Currencies of open or closed accounts that differ from the budget's. */
export async function getForeignAccountCurrencies(
  budgetCurrency: string,
): Promise<string[]> {
  const rows = await db.all<{ currency: string }>(
    `SELECT DISTINCT currency FROM accounts
      WHERE tombstone = 0
        AND currency IS NOT NULL AND currency != '' AND currency != ?
      ORDER BY currency`,
    [budgetCurrency],
  );
  return rows.map(r => r.currency);
}

/**
 * Fetch the rate of every foreign account currency into the budget currency
 * and store one row per currency for the day. Existing provider rows for
 * that day are refreshed in place; a manually entered rate for the same
 * pair and day is left alone so user overrides always win.
 */
async function fetchRates({
  date,
}: FetchRatesArgs = {}): Promise<FetchRatesResult> {
  const server = await serverHeaders();
  if ('error' in server) {
    return { ok: false, error: server.error };
  }

  const budgetCurrency = await getBudgetCurrency();
  if (!budgetCurrency) {
    return { ok: false, error: 'no-budget-currency' };
  }
  const symbols = await getForeignAccountCurrencies(budgetCurrency);
  if (symbols.length === 0) {
    return { ok: false, error: 'no-foreign-accounts' };
  }

  let data: {
    error_code?: string;
    reason?: string;
    base?: string;
    date?: string;
    rates?: Record<string, number>;
  };
  try {
    data = await post(
      server.url + '/rates',
      { base: budgetCurrency, symbols, ...(date ? { date } : {}) },
      server.headers,
    );
  } catch (e) {
    return {
      ok: false,
      error: 'request-failed',
      reason: e instanceof PostError ? e.reason : String(e),
    };
  }
  if (!data || data.error_code) {
    return {
      ok: false,
      error: data?.error_code ?? 'request-failed',
      reason: data?.reason,
    };
  }

  // The latest rates are filed under the client's current day so the
  // once-a-day check lines up with what the user sees in the register.
  const day = date ?? currentDay();
  const dayRepr = toDateRepr(day);
  const rates = data.rates ?? {};
  const currencies: string[] = [];
  const skipped: string[] = [];

  await runMutator(async () => {
    await batchMessages(async () => {
      for (const code of symbols) {
        const rate = rates[code];
        if (!(rate > 0)) {
          continue;
        }
        const existing = await db.all<{ id: string; source: string | null }>(
          `SELECT id, source FROM exchange_rates
            WHERE tombstone = 0 AND source_currency = ? AND target_currency = ?
              AND date = ?`,
          [code, budgetCurrency, dayRepr],
        );
        const manual = existing.find(
          r => r.source !== OPENEXCHANGERATES_SOURCE,
        );
        if (manual) {
          skipped.push(code);
          continue;
        }
        const ours = existing.find(r => r.source === OPENEXCHANGERATES_SOURCE);
        if (ours) {
          await db.updateExchangeRate({ id: ours.id, rate });
        } else {
          await db.insertExchangeRate({
            source_currency: code,
            target_currency: budgetCurrency,
            rate,
            date: dayRepr,
            source: OPENEXCHANGERATES_SOURCE,
          });
        }
        currencies.push(code);
      }
    });
  });

  if (currencies.length > 0) {
    recomputeBudget();
  }

  return { ok: true, date: day, base: budgetCurrency, currencies, skipped };
}

async function getSyncedPref(id: string): Promise<string | null> {
  const row = await db.first<{ value: string | null }>(
    `SELECT value FROM preferences WHERE id = ?`,
    [id],
  );
  return row?.value ?? null;
}

let autoFetchInFlight = false;
let lastAutoFetchAttempt = 0;

/**
 * Once a day, after the first successful sync, pull the latest rates. Runs
 * in the background; failures are logged and retried on a later sync (at
 * most hourly), and the day is only marked done after rates were stored.
 */
export async function autoFetchRates(): Promise<void> {
  if (autoFetchInFlight) {
    return;
  }
  const now = Date.now();
  if (now - lastAutoFetchAttempt < AUTO_FETCH_RECHECK_MS) {
    return;
  }
  autoFetchInFlight = true;
  lastAutoFetchAttempt = now;
  try {
    if ((await getSyncedPref('exchangeRatesAutoFetch')) === 'false') {
      return;
    }
    const status = await providerStatus();
    if (!status.configured) {
      return;
    }
    const result = await fetchRates();
    // `=== false` rather than `!ok`: loot-core compiles without strictNullChecks,
    // where truthiness checks do not narrow the union.
    if (result.ok === false) {
      if (
        result.error === 'no-foreign-accounts' ||
        result.error === 'no-budget-currency'
      ) {
        // Nothing to fetch for this budget today.
        await prefs.savePrefs({ lastExchangeRateFetch: currentDay() });
        return;
      }
      logger.warn(
        `Automatic exchange-rate fetch failed: ${result.error} ${result.reason ?? ''}`,
      );
      return;
    }
    logger.info(
      `Fetched exchange rates for ${result.date}: ${result.currencies.join(', ') || '(none)'}`,
    );
    await prefs.savePrefs({ lastExchangeRateFetch: currentDay() });
  } catch (e) {
    logger.warn('Automatic exchange-rate fetch failed', e);
  } finally {
    autoFetchInFlight = false;
  }
}

/** Test hook: forget the hourly retry throttle. */
export function resetAutoFetchThrottle() {
  lastAutoFetchAttempt = 0;
}

app.events.on('sync', ({ type }) => {
  if (type !== 'success') {
    return;
  }
  const loaded = prefs.getPrefs();
  if (!loaded || !db.getDatabase()) {
    return;
  }
  if (loaded.lastExchangeRateFetch === currentDay()) {
    return;
  }
  void autoFetchRates();
});

// ---------------------------------------------------------------------------
// Historical backfill: a transaction far from every stored rate triggers a
// fetch of that day's rate, so its converted amount uses a nearby rate.
// ---------------------------------------------------------------------------

export const DEFAULT_BACKFILL_MAX_GAP_DAYS = 7;
/** Open Exchange Rates has no data before this day. */
const EARLIEST_HISTORICAL_DATE = '1999-01-01';
/** Safety cap on API calls a single batch of transactions may cause. */
export const MAX_BACKFILL_FETCHES_PER_BATCH = 50;
/** Don't re-ask the server whether the provider is configured more often. */
const BACKFILL_STATUS_RECHECK_MS = 60 * 60 * 1000;

type BackfillCandidate = {
  /** yyyy-MM-dd */
  date: string;
  account: string;
};

/**
 * Days between `date` and the nearest stored rate for the pair (either
 * direction), or null when no rate is stored at all.
 */
export async function nearestRateGapDays(
  from: string,
  to: string,
  date: string,
): Promise<number | null> {
  const row = await db.first<{ gap: number | null }>(
    `SELECT MIN(ABS(${julianDayOfInt('r.date')} - julianday(?))) AS gap
       FROM exchange_rates r
      WHERE r.tombstone = 0
        AND ((r.source_currency = ? AND r.target_currency = ?)
          OR (r.source_currency = ? AND r.target_currency = ?))`,
    [date, from, to, to, from],
  );
  return row?.gap ?? null;
}

async function backfillSettings(): Promise<{
  enabled: boolean;
  maxGapDays: number;
}> {
  const enabled = (await getSyncedPref('exchangeRatesBackfill')) !== 'false';
  const raw = await getSyncedPref('exchangeRatesBackfillMaxGapDays');
  const parsed = raw == null || raw === '' ? NaN : Number(raw);
  const maxGapDays =
    Number.isFinite(parsed) && parsed >= 0
      ? Math.floor(parsed)
      : DEFAULT_BACKFILL_MAX_GAP_DAYS;
  return { enabled, maxGapDays };
}

let providerConfiguredCache: { at: number; configured: boolean } | null = null;

async function isProviderConfigured(): Promise<boolean> {
  const now = Date.now();
  if (
    providerConfiguredCache &&
    now - providerConfiguredCache.at < BACKFILL_STATUS_RECHECK_MS
  ) {
    return providerConfiguredCache.configured;
  }
  const status = await providerStatus();
  providerConfiguredCache = { at: now, configured: status.configured };
  return status.configured;
}

/** Test hook: forget the cached provider status. */
export function resetBackfillCache() {
  providerConfiguredCache = null;
}

// One backfill runs at a time; later batches wait for the current one so
// their gap checks see the rates it stored.
let backfillChain: Promise<void> = Promise.resolve();

/**
 * Called after transactions were added or updated. Returns a promise that
 * settles when the (background) work is done; callers on the write path
 * should not await it.
 */
export function backfillRatesForTransactions(
  transactions: BackfillCandidate[],
): Promise<void> {
  const run = backfillChain.then(() => runBackfill(transactions));
  backfillChain = run.catch(() => undefined);
  return run;
}

async function runBackfill(transactions: BackfillCandidate[]): Promise<void> {
  if (transactions.length === 0 || !db.getDatabase()) {
    return;
  }
  const { enabled, maxGapDays } = await backfillSettings();
  if (!enabled) {
    return;
  }
  const budgetCurrency = await getBudgetCurrency();
  if (!budgetCurrency) {
    return;
  }

  const accounts = await db.all<{ id: string; currency: string | null }>(
    'SELECT id, currency FROM accounts WHERE tombstone = 0',
  );
  const currencyByAccount = new Map(accounts.map(a => [a.id, a.currency]));

  // date -> foreign currencies used on that day
  const today = currentDay();
  const wanted = new Map<string, Set<string>>();
  for (const t of transactions) {
    const currency = currencyByAccount.get(t.account);
    if (!currency || currency === budgetCurrency) {
      continue;
    }
    if (
      !t.date ||
      t.date > today ||
      t.date < EARLIEST_HISTORICAL_DATE ||
      !/^\d{4}-\d{2}-\d{2}$/.test(t.date)
    ) {
      continue;
    }
    let set = wanted.get(t.date);
    if (!set) {
      set = new Set();
      wanted.set(t.date, set);
    }
    set.add(currency);
  }
  if (wanted.size === 0) {
    return;
  }

  let providerChecked = false;
  let fetches = 0;
  for (const date of [...wanted.keys()].sort()) {
    // Re-check right before fetching: an earlier fetch in this loop may
    // already have covered this day.
    let needed = false;
    for (const currency of wanted.get(date) ?? []) {
      const gap = await nearestRateGapDays(currency, budgetCurrency, date);
      if (gap == null || gap > maxGapDays) {
        needed = true;
        break;
      }
    }
    if (!needed) {
      continue;
    }
    if (!providerChecked) {
      if (!(await isProviderConfigured())) {
        return;
      }
      providerChecked = true;
    }
    if (fetches >= MAX_BACKFILL_FETCHES_PER_BATCH) {
      logger.warn(
        `Exchange-rate backfill stopped after ${fetches} fetches; enter more transactions or fetch remaining dates by hand`,
      );
      return;
    }
    fetches++;
    const result = await fetchRates({ date });
    if (result.ok === false) {
      logger.warn(
        `Exchange-rate backfill for ${date} failed: ${result.error} ${result.reason ?? ''}`,
      );
      // A provider/server problem will hit every later date too.
      if (result.error !== 'no-foreign-accounts') {
        return;
      }
    } else {
      logger.info(
        `Backfilled exchange rates for ${date}: ${result.currencies.join(', ') || '(none)'}`,
      );
    }
  }
}
